'use client';
import { SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { Mail, Lock, ArrowRight } from 'lucide-react';
import { authClient } from '@/lib/better-auth/auth-client';

const SignInUserSchema = z.object({
  email: z.email(),
  password: z.string().min(1, { message: 'Please enter a password' }),
});

type SignInUserSchemaType = z.infer<typeof SignInUserSchema>;

export const GlobalAdminSignInForm = () => {
  const router = useRouter();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<SignInUserSchemaType>({
    resolver: zodResolver(SignInUserSchema),
    mode: 'onChange',
  });

  const onSubmit: SubmitHandler<SignInUserSchemaType> = async formData => {
    const { error } = await authClient.signIn.email({
      email: formData.email,
      password: formData.password,
    });
    if (error) {
      setError('root', { message: error.message ?? 'Invalid email or password' });
      return;
    }
    router.push('/admin/dashboard');
  };

  return (
    <div className="flex flex-col gap-[28px]">
      {/* Heading */}
      <div className="flex flex-col gap-[6px]">
        <h1 className="text-[26px] font-bold tracking-[-0.52px] text-[#161320]">
          Sign in to your account
        </h1>
        <p className="text-[14px] text-[#8b86a0]">
          Enter your credentials to access the admin console.
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-[16px]">
        {/* Email */}
        <div className="flex flex-col gap-[6px]">
          <label className="text-[13.5px] font-semibold tracking-[-0.135px] text-[#161320]">
            Email
          </label>
          <div className="relative">
            <Mail className="absolute left-[14px] top-1/2 size-[15px] -translate-y-1/2 text-[#8b86a0]" />
            <input
              {...register('email')}
              type="email"
              placeholder="admin@noctocode.com"
              className="h-[48px] w-full rounded-[14px] border border-[#e7e4ef] bg-[#f8f7fb] pl-[40px] pr-[14px] text-[14px] tracking-[-0.084px] text-[#161320] outline-none placeholder:text-[#c4bfd6] focus:border-[#6c3cd9]"
            />
          </div>
          {errors.email && (
            <p className="text-[12px] text-[#e11d48]">{errors.email.message}</p>
          )}
        </div>

        {/* Password */}
        <div className="flex flex-col gap-[6px]">
          <label className="text-[13.5px] font-semibold tracking-[-0.135px] text-[#161320]">
            Password
          </label>
          <div className="relative">
            <Lock className="absolute left-[14px] top-1/2 size-[15px] -translate-y-1/2 text-[#8b86a0]" />
            <input
              {...register('password')}
              type="password"
              placeholder="••••••••••"
              className="h-[48px] w-full rounded-[14px] border border-[#e7e4ef] bg-[#f8f7fb] pl-[40px] pr-[14px] text-[14px] tracking-[-0.084px] text-[#161320] outline-none placeholder:text-[#c4bfd6] focus:border-[#6c3cd9]"
            />
          </div>
          {errors.password && (
            <p className="text-[12px] text-[#e11d48]">{errors.password.message}</p>
          )}
          <Link
            href="/admin/auth/forgot-password"
            className="self-end text-[12.5px] font-semibold text-[#6c3cd9] hover:underline"
          >
            Forgot your password?
          </Link>
        </div>

        {errors.root && (
          <p className="text-[13px] text-[#e11d48]">{errors.root.message}</p>
        )}

        {/* Submit */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="mt-[4px] flex h-[50px] w-full cursor-pointer items-center justify-center gap-[8px] rounded-[14px] bg-[#6c3cd9] transition-opacity hover:opacity-90 disabled:opacity-60"
        >
          <span className="text-[15px] font-semibold tracking-[-0.15px] text-white">
            {isSubmitting ? 'Signing in…' : 'Sign in'}
          </span>
          {!isSubmitting && <ArrowRight className="size-[16px] text-white" />}
        </button>
      </form>

      {/* Footer */}
      <p className="text-center text-[12.5px] text-[#8b86a0]">
        Protected admin area ·{' '}
        <span className="font-semibold text-[#6c3cd9]">Perception AI</span>
      </p>
    </div>
  );
};
